import type { MiddlewareAPI } from 'redux';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import { catchError, switchMap } from 'rxjs/operators';
import { ff } from '@atlassian/jira-feature-flagging';
import { fetchJson$ } from '@atlassian/jira-fetch';
import type { PersonalizationAPIResponse } from '@atlassian/jira-issue-shared-types';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type';
import { trackOrLogClientError } from '@atlassian/jira-issue-view-common-utils';
import {
	SET_CONTEXT,
	type SetContext,
} from '@atlassian/jira-issue-view-store/src/actions/context-actions';
import { personalizationTapTraitConfluenceActivationFetched } from '@atlassian/jira-issue-view-store/src/actions/personalization-fetch-tap-trait-confluence-activation-action';
import { cloudIdSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/empty';
import 'rxjs/add/operator/switchMap';

const PERSONALIZATION_BASE_URL = '/gateway/api/tap-delivery/api/v3/personalization';
const SITE_QUERY_PARAM = '/site/';
const LOG_LOCATION = 'issue.fetch.personalization-tap-traits-confluence-activation-epic';

const CONFLUENCE_ACTIVATION_DATE_TAP_TRAIT = 'confluence.ondemand.activationDate' as const;

function getUserPersonalizationUrl(cloudId: string) {
	return PERSONALIZATION_BASE_URL + SITE_QUERY_PARAM + cloudId;
}

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<SetContext>, store: MiddlewareAPI<State>) =>
	action$.ofType(SET_CONTEXT).switchMap(() => {
		if (ff('aa_confluence_placeholder_template_onboarding_1l96p')) {
			const state: State = store.getState();
			const cloudId = cloudIdSelector(state);

			if (!cloudId) return Observable.empty<never>();

			return fetchJson$<PersonalizationAPIResponse | null>(getUserPersonalizationUrl(cloudId)).pipe(
				switchMap((response) => {
					if (response?.attributes) {
						const attribute = response.attributes.find(
							(attr) => attr.name === CONFLUENCE_ACTIVATION_DATE_TAP_TRAIT,
						);
						if (attribute && typeof attribute.value === 'string') {
							return Observable.of(
								personalizationTapTraitConfluenceActivationFetched(attribute.value),
							);
						}
					}
					return Observable.empty<never>();
				}),
				catchError((error) => {
					trackOrLogClientError(
						LOG_LOCATION,
						'Failed to fetch TAP traits via the personalization api',
						error,
					);
					return Observable.empty<never>();
				}),
			);
		}
		return Observable.empty<never>();
	});
